import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {RouteComponentProps} from "react-router";
import {lazyInject} from "@athena-portal/core";
import {setSessionStorage, getSessionStorage, getAddress, getModifiedMobileNumber} from "../../../../../CommonFunctions";
import {OnlineMotorClaimRequest, Types, ICustomerPortalService, Policy, Party, FalconNavigatorHeader, SESSION_STORAGE_KEY_CLAIM_POLICY} from "@falcon/services";
import {BackButton} from "../../../../../components/navigation/BackButton";
import {NextButton} from "./NextButton";
import {ChangeContactDetailsPopup} from "./ChangeContactDetailsPopup";
import * as moment from "moment";

interface IProps extends RouteComponentProps<{}> {}

interface ICoverDetailsPageState {
    onlineMotorClaimRequest: OnlineMotorClaimRequest;
    policyDetails: IPolicyDetails;
}

export interface IPolicyDetails {
    selectedPolicy?: Policy;
    showEditContactDetailsPopup: boolean;
    party: Party | null;
    partyLoaded: boolean;
}

@authorize
export class CoverDetailsPage extends React.Component<IProps, ICoverDetailsPageState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);

        const onlineMotorClaimRequest: OnlineMotorClaimRequest = getSessionStorage("onlineMotorClaimRequest");
        const policyDetails: IPolicyDetails = getSessionStorage("policyDetails");
        const selectedPolicy = getSessionStorage(SESSION_STORAGE_KEY_CLAIM_POLICY);

        this.state = {
            onlineMotorClaimRequest: onlineMotorClaimRequest !== null ? onlineMotorClaimRequest : new OnlineMotorClaimRequest(),
            policyDetails: policyDetails !== null ? policyDetails : {
                selectedPolicy,
                showEditContactDetailsPopup: false,
                party: null,
                partyLoaded: false
            } as IPolicyDetails
        };
        this.pluginTemplates.setPageTitle("Make a car claim");
    }

    public componentDidMount(): void {
        window.scrollTo(0, 0);

        const policyDetails: IPolicyDetails = {...this.state.policyDetails};
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            onlineMotorClaimRequest.contactEmail = onlineMotorClaimRequest.contactEmail !== undefined ? onlineMotorClaimRequest.contactEmail : output.email;
            onlineMotorClaimRequest.contactMobile = onlineMotorClaimRequest.contactMobile !== undefined ? onlineMotorClaimRequest.contactMobile : output.mobileNumber;
            onlineMotorClaimRequest.firstName = output.firstName;
            onlineMotorClaimRequest.lastName = output.lastName;
            onlineMotorClaimRequest.dateOfBirth = moment(output.dateOfBirth).format("DD-MM-YYYY");
            onlineMotorClaimRequest.fullAddress = `${getAddress(output.address)} ${output.address.postalCode}`;
            policyDetails.party = output;
            policyDetails.partyLoaded = true;
        });

        onlineMotorClaimRequest.claimPolicyNumber = policyDetails.selectedPolicy!.policyNumber;

        this.setState({onlineMotorClaimRequest, policyDetails});
        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
        setSessionStorage("policyDetails", policyDetails);
    }

    public render() {
        const {party, partyLoaded} = this.state.policyDetails;

        if (partyLoaded) {
            return (
                <React.Fragment>
                    <FalconHeader/>

                    <div className="page-contents">
                        <ChangeContactDetailsPopup showModal={this.state.policyDetails.showEditContactDetailsPopup}
                                                   onHide={this.toggleModal}
                                                   onChangeAccountDetails={this.handleAccountDetailsChange}/>
                        <FalconNavigatorHeader
                            back="/dashboard/windscreen/urgent"
                            title="Make a car claim"
                        />
                        <div className="section-panel">
                            <img className="main-icon" src="/img/Flow-make-a-claim.svg" alt="Make a renters claim"
                                 aria-label="Make a renters claim" tabIndex={0}/>
                            {this.renderPolicyDetailsSection(party!)}
                        </div>
                        {this.renderOtherDetailsPanel()}
                        <BackButton isTop={false}/>
                    </div>

                    <FalconFooter/>
                </React.Fragment>
            )
        } else {
            return (<Overlay show={true}/>)
        }
    }

    private renderPolicyDetailsSection = (party: Party): JSX.Element => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};

        return (
            <React.Fragment>
                <div className="section">
                    <div className="section-heading">Cover details</div>
                    <div className="section-contents">
                        <div className="detail">
                            <div className="detail-key font-weight-semi-bold">Account holder</div>
                            <div className="detail-value" id="accountHolderFullName">{party.firstName} {party.lastName}</div>
                        </div>
                        <div className="detail">
                            <div className="detail-key font-weight-semi-bold">Insured address</div>
                            <div className="detail-value" id="accountHolderInsuredAddress">{getAddress(party.address)} {party.address.postalCode}</div>
                        </div>
                    </div>
                </div>
                <div className="section">
                    <div className="section-heading">Check contact details</div>
                    <div className="section-contents">
                        <div className="detail">
                            <div className="detail-key font-weight-semi-bold">Mobile number</div>
                            <div className="detail-value" id="accountPhoneNumber">{getModifiedMobileNumber(onlineMotorClaimRequest.contactMobile)}</div>
                        </div>
                        <div className="detail">
                            <div className="detail-key font-weight-semi-bold">Email</div>
                            <div className="detail-value" id="accountEmailAddress">{onlineMotorClaimRequest.contactEmail}</div>
                        </div>
                    </div>
                    <div className="section-links">
                        <a id="editMotorContactDetails" className="buttonLink">
                            <button type="button" className="btn btn-white-dashboard w-100"
                                    onClick={this.toggleModal} id="changeContactDetailsButton">
                                Change contact details</button>
                        </a>
                    </div>
                </div>
            </React.Fragment>
        )
    };

    private renderOtherDetailsPanel = (): JSX.Element => {
        return (
            <React.Fragment>
                <p className="font-weight-semi-bold fs-18">A few things to note before you make your claim:</p>
                <ul>
                    <li>We share data and record suspicions of fraud with other organisations, fraud prevention agencies and their members to prevent financial crime</li>
                    <li>To help us and the supplier we may appoint to handle your claim, we'll also share information with them so that we can provide the best possible service</li>
                    <li>If your claim is urgent, you can call us on{' '}
                        <a className="a-bold" href="tel:+44 8000 304 897" aria-label="Phone number +44 8000 304 897">08000 304 897</a> or{' '}
                        <a className="a-bold" href="tel:+44 120 284 8509" aria-label="Phone number +44 120 284 8509">(+44) 0120 284 8509</a> 24/7</li>
                </ul>
                <NextButton buttonId={"submitPolicyDetailsPage"}
                            disabled={false}
                            onClick={this.handleRedirect}/>
            </React.Fragment>
        )
    };

    private handleRedirect = (): void => {
        this.props.history.push("/dashboard/windscreen/urgent/coverDetails/occur");
    };

    private toggleModal = (): void => {
        const policyDetails: IPolicyDetails = {...this.state.policyDetails};
        policyDetails.showEditContactDetailsPopup = !policyDetails.showEditContactDetailsPopup;
        this.setState({policyDetails});
        setSessionStorage("policyDetails", policyDetails);
    };

    private handleAccountDetailsChange = (details: {email: string, phoneNumber: string, showPopup: boolean}): void => {
        const policyDetails: IPolicyDetails = {...this.state.policyDetails};
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        onlineMotorClaimRequest.contactMobile = details.phoneNumber;
        onlineMotorClaimRequest.contactEmail = details.email;
        policyDetails.showEditContactDetailsPopup = details.showPopup;
        this.setState({policyDetails, onlineMotorClaimRequest});
        setSessionStorage("policyDetails", policyDetails);
        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
    };
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/subscription/motor/motorClaims/CoverDetailsPage.tsx